import { useState, useEffect, useCallback } from 'react';
import { X, Search, Loader2, User } from 'lucide-react';
import { clientesService } from '@/services/clientes.service';
import { useAuthStore } from '@/stores/auth-store';

interface CustomerSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: any) => void;
}

export function CustomerSelectionModal({ isOpen, onClose, onSelect }: CustomerSelectionModalProps) {
  const { user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCustomers = useCallback(async (search: string) => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const data = await clientesService.findAll(user.company.id, search);
      setCustomers(data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      loadCustomers('');
      setSearchTerm('');
    }
  }, [isOpen, loadCustomers]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isOpen) {
        loadCustomers(searchTerm);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, loadCustomers, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex bg-black/50 p-4 sm:p-6 lg:p-8 items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 text-[#007AFF] rounded-2xl flex items-center justify-center">
              <User size={24} strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Asignar Cliente</h2>
              <p className="text-sm text-gray-500">Busca y selecciona un cliente</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100 bg-gray-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nombre, correo o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="p-8 flex justify-center items-center">
              <Loader2 className="animate-spin text-gray-400" size={24} />
            </div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="text-gray-300" size={24} />
              </div>
              <p className="text-gray-500 font-medium">No se encontraron clientes</p>
              <p className="text-xs text-gray-400 mt-1">Prueba con otra búsqueda</p>
            </div>
          ) : (
            <div className="space-y-1">
              {customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    onSelect(c);
                    onClose();
                  }}
                  className="w-full text-left p-3 hover:bg-blue-50 rounded-xl transition-colors flex items-center gap-3 group"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
                    <User size={18} />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="font-bold text-gray-700 group-hover:text-blue-700 truncate">{c.name}</p>
                    {(c.email || c.phone) && (
                      <p className="text-xs text-gray-500 truncate">
                        {c.email} {c.email && c.phone && '•'} {c.phone}
                      </p>
                    )}
                  </div>
                  {Number(c.creditLimit) > 0 && (
                    <div className="text-right flex-shrink-0">
                      <p className={`text-xs font-bold ${Number(c.creditUsed) >= Number(c.creditLimit) ? 'text-red-500' : 'text-green-600'}`}>
                        ${Number(c.creditUsed).toFixed(0)} / ${Number(c.creditLimit).toFixed(0)}
                      </p>
                      <p className="text-[9px] text-gray-400">
                        {Number(c.creditUsed) >= Number(c.creditLimit) ? 'LÍMITE ALCANZADO' : `${Math.round((1 - Number(c.creditUsed)/Number(c.creditLimit)) * 100)}% disp.`}
                      </p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
